"use client";

import {
  AssetsSparkSurface,
  PageSizeProvider,
  useRouterParams,
  Pagination,
  usePageSize,
} from "@zapier/shared";
import { EmptyTemplatesState } from "./TemplatesTable/EmptyTemplatesState";
import { EmptyTemplatesStateTypes } from "./TemplatesTable/EmptyTemplatesState.types";
import { useShowEmptyTemplatesTable } from "./TemplatesTable/useShowEmptyTemplatesTable";
import { TemplatesHeader } from "./TemplatesHeader/TemplatesHeader";
import TemplatesTable from "./TemplatesTable/TemplatesTable";
import { useTemplatesList } from "./TemplatesTable/useTemplatesList";
import { SortDescriptor } from "@zapier/design-system";
import { useState } from "react";

function TemplatesWithPageSize() {
  const { size: pageSize } = usePageSize();
  const [sortDescriptor, setSortDescriptor] = useState<SortDescriptor>({
    column: "name",
    direction: "ascending",
  });

  const { templates, isLoading, isFetching, page, totalPages, totalResults } =
    useTemplatesList({ pageSize });

  const { search, shared, app, owner } = useRouterParams();
  const hasSharedFilter = shared && shared !== "all";
  const isFiltered = !!(search || app || owner || hasSharedFilter);

  const { showEmptyState, emptyType } = useShowEmptyTemplatesTable({
    templates,
    isLoading,
    isFiltered,
    shared,
  });

  const handleSortChange = (newSortDescriptor: SortDescriptor) => {
    setSortDescriptor(newSortDescriptor);
    // TODO: Implement sorting logic when backend supports it
  };

  return (
    <>
      {/* Hide search/filters only when showing empty state for no templates / owned / shared; keep them visible for "no results" (EmptySearch) so user can adjust filters */}
      <TemplatesHeader
        showSearch={
          !showEmptyState || emptyType === EmptyTemplatesStateTypes.EmptySearch
        }
      />
      {(isLoading || isFetching || templates.length > 0) && (
        <AssetsSparkSurface
          surfaceId={process.env.NEXT_PUBLIC_SPARK_SURFACE_ID_TEMPLATES}
        />
      )}
      {showEmptyState && emptyType ? (
        <EmptyTemplatesState tableType={emptyType} />
      ) : (
        <>
          <TemplatesTable
            templates={templates}
            isLoading={isLoading || isFetching}
            sortDescriptor={sortDescriptor}
            onSortChange={handleSortChange}
          />
          <Pagination
            page={page}
            totalPages={totalPages}
            totalCount={totalResults}
            isLoading={isLoading || isFetching}
          />
        </>
      )}
    </>
  );
}

/**
 * Wrap this component in PageSizeProvider so that the context is available
 * to the Table and Pagination components above.
 */
export function Templates() {
  return (
    <PageSizeProvider>
      <TemplatesWithPageSize />
    </PageSizeProvider>
  );
}
